import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCamera } from '../shared-camera/hooks/useCamera';
import { useFrameSampler, type SampledFrame } from '../continuous-scan/hooks/useFrameSampler';
import { useCycleCountSocket } from './hooks/useCycleCountSocket';
import { MetricsPanel } from './components/MetricsPanel';
import { ResultsTable } from './components/ResultsTable';
import type { CodeSummary, CycleCountMetrics, LlmStatus } from './lib/metricsTypes';
import { getToken } from '@/lib/auth';

// Cycle Count page. Sits inside MainLayout (nav bar stays visible) but
// its content fills the remaining viewport with no scroll. Camera view
// is the default background while streaming; on stop it swaps for a
// live-updating results view that keeps refreshing as the async LLM
// tail drains before the WS closes.

const BACKGROUNDED_PAUSE_WINDOW_MS = 30_000;

// Client-side backpressure ceiling. We sample frames faster than a phone
// uplink can upload them; socket.send() just queues into bufferedAmount. If we
// let that grow unbounded the buffer keeps flushing to the server long after
// Stop (the "received count climbs after we stopped" symptom). Skip sending
// whenever the outbound buffer already holds ~one server-max frame, so what's
// in flight stays tiny and Stop takes effect right away.
const MAX_WS_BUFFER_BYTES = 512 * 1024;

function resolveWsUrl(): string {
  // Prefer VITE_API_URL when set (QA/prod builds). Azure Static Web Apps'
  // linked-backend proxy does NOT forward WebSocket upgrades — hitting the
  // SWA hostname for a wss:// handshake gets 101 → immediate close. We
  // have to talk directly to the App Service that hosts the WS endpoint.
  // In local dev VITE_API_URL is empty and we fall back to location.host
  // so Vite's ws:true proxy carries the upgrade.
  const apiBase = import.meta.env.VITE_API_URL || '';
  const token = getToken();
  const query = token ? `?access_token=${encodeURIComponent(token)}` : '';

  if (apiBase) {
    const wsBase = apiBase.replace(/^http:\/\//, 'ws://').replace(/^https:\/\//, 'wss://');
    return `${wsBase.replace(/\/$/, '')}/api/v1/cycle-count/ws${query}`;
  }

  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/api/v1/cycle-count/ws${query}`;
}

type UiState = 'idle' | 'streaming' | 'paused' | 'backgrounded_stopped' | 'stopping' | 'closed';

// Live rollup of per-code state, populated from `count` + `llm_result`
// events as they arrive. The server's final `metrics` event replaces this
// with the authoritative snapshot.
interface LiveCode {
  code: string;
  frameCount: number;
  itemCount: number | null;
  llmStatus: LlmStatus;
}

function toCodeSummary(c: LiveCode): CodeSummary {
  return { code: c.code, frameCount: c.frameCount, itemCount: c.itemCount, llmStatus: c.llmStatus };
}

// Coerce the LLM's raw result payload into an item count. Server pushes
// `{ code, result: InferenceRunModel }`; the count sits in
// `result.Results[0].Count`.
function extractItemCount(result: unknown): number | null {
  const r = result as { results?: Array<{ count?: number | null }> } | null | undefined;
  const first = r?.results?.[0];
  return first?.count ?? null;
}

export function CycleCountPage() {
  const camera = useCamera();
  const { status: socketStatus, start, stop, sendBinary, getBufferedAmount, onEvent } =
    useCycleCountSocket();
  const [startBusy, setStartBusy] = useState(false);
  const [count, setCount] = useState(0);
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [serverDrops, setServerDrops] = useState(0);
  const [uiState, setUiState] = useState<UiState>('idle');
  const [metrics, setMetrics] = useState<CycleCountMetrics | null>(null);
  // Frame throughput from the server's `progress` event: frames received off
  // the wire vs. frames a worker has decoded. The backlog (received -
  // processed) is what the operator watches drain to zero to know the scan
  // has cleared.
  const [progress, setProgress] = useState<{ framesReceived: number; framesProcessed: number } | null>(null);
  // Client-side upload view: frames we've handed to the socket (cumulative)
  // and how many bytes are still sitting in its outbound buffer. Shown next to
  // the server counts so it's clear that after Stop the server is just
  // catching up to what we already sent — not receiving anything new.
  const uploadedRef = useRef(0);
  const [clientStats, setClientStats] = useState<{ uploaded: number; bufferedBytes: number }>({
    uploaded: 0,
    bufferedBytes: 0,
  });
  // Live rollup keyed by decoded value. Updated on every count/llm_result
  // event so the operator sees the results table filling in while the
  // async LLM tail drains post-Stop.
  const [liveCodes, setLiveCodes] = useState<Map<string, LiveCode>>(new Map());
  const [debugLog, setDebugLog] = useState<string[]>([]);
  const appendDebug = useCallback((line: string) => {
    setDebugLog((prev) => [...prev, `${new Date().toLocaleTimeString()} ${line}`].slice(-60));
  }, []);
  const pauseTimerRef = useRef<number | null>(null);
  const uiStateRef = useRef<UiState>('idle');
  const mountedRef = useRef(true);

  useEffect(() => {
    uiStateRef.current = uiState;
  }, [uiState]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    void camera.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.start]);

  // Tune the camera for scanning distance once it's ready. Target ~0.75m
  // (about 2.5 feet) — the typical distance an operator holds a phone
  // from a bin of coded items. Best-effort: iOS Safari doesn't expose
  // focusMode/focusDistance today, so this often no-ops there; on
  // Android Chrome (S9+) it usually applies.
  useEffect(() => {
    if (camera.status !== 'ready') return;
    const stream = camera.videoRef.current?.srcObject as MediaStream | null;
    const track = stream?.getVideoTracks()[0];
    if (!track) return;
    if (typeof track.getCapabilities !== 'function') {
      appendDebug('focus: getCapabilities unsupported');
      return;
    }
    // Cast to any because TS lib.dom hasn't caught up with WICG focus
    // capabilities. See https://w3c.github.io/mediacapture-image/.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const caps = track.getCapabilities() as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const advanced: Record<string, any> = {};

    if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
      advanced.focusMode = 'continuous';
    }
    if (caps.focusDistance && typeof caps.focusDistance.min === 'number') {
      // ~0.75m target — the reachable range on real devices is often
      // 0.1m..3m; clamp to the reported range so applyConstraints doesn't
      // reject the whole call.
      const target = 0.75;
      const min = caps.focusDistance.min;
      const max = caps.focusDistance.max ?? target;
      advanced.focusDistance = Math.min(max, Math.max(min, target));
    }

    if (Object.keys(advanced).length === 0) {
      appendDebug(`focus: no capabilities (caps=${JSON.stringify(Object.keys(caps))})`);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    track.applyConstraints({ advanced: [advanced] } as any)
      .then(() => appendDebug(`focus applied ${JSON.stringify(advanced)}`))
      .catch((err) => appendDebug(`focus failed: ${(err as Error).message}`));
  }, [camera.status, camera.videoRef, appendDebug]);

  useEffect(() => {
    onEvent((event) => {
      if (!mountedRef.current) return;
      switch (event.type) {
        case 'count':
          setCount(event.n);
          setLastCode(event.code);
          setLiveCodes((prev) => {
            if (prev.has(event.code)) return prev;
            const next = new Map(prev);
            next.set(event.code, {
              code: event.code,
              frameCount: 1,
              itemCount: null,
              llmStatus: 'pending',
            });
            return next;
          });
          break;
        case 'backpressure':
          setServerDrops((s) => s + event.droppedSinceLast);
          appendDebug(`backpressure dropped=${event.droppedSinceLast}`);
          break;
        case 'llm_result':
          setLiveCodes((prev) => {
            const next = new Map(prev);
            const existing = next.get(event.code) ?? {
              code: event.code,
              frameCount: 0,
              itemCount: null,
              llmStatus: 'pending' as LlmStatus,
            };
            next.set(event.code, {
              ...existing,
              itemCount: extractItemCount(event.result),
              llmStatus: 'completed',
            });
            return next;
          });
          appendDebug(`llm_result ${event.code}`);
          break;
        case 'progress':
          setProgress({ framesReceived: event.framesReceived, framesProcessed: event.framesProcessed });
          appendDebug(`progress frames ${event.framesProcessed}/${event.framesReceived}`);
          break;
        case 'metrics':
          setMetrics(event.snapshot);
          appendDebug(`metrics codes=${event.snapshot?.codes?.length ?? 'undef'} unique=${event.snapshot?.uniqueBins}`);
          break;
        case 'hello':
          appendDebug(`hello runId=${event.serverTime}`);
          break;
        default:
          appendDebug(`unknown event`);
          break;
      }
    });
  }, [onEvent, appendDebug]);

  useEffect(() => {
    appendDebug(`socket=${socketStatus} ui=${uiState}`);
    if (socketStatus === 'closed' && uiState !== 'idle' && uiState !== 'closed') {
      setUiState('closed');
    }
  }, [socketStatus, uiState, appendDebug]);

  // Surface the client-side upload counters for display. Polled (not updated
  // per-frame) so a 15-60 fps send loop doesn't thrash React. Runs while a run
  // is live or draining so the operator can watch `buffered` fall to zero
  // after Stop; idle when there's nothing to show.
  useEffect(() => {
    if (uiState === 'idle') return;
    const id = window.setInterval(() => {
      setClientStats({ uploaded: uploadedRef.current, bufferedBytes: getBufferedAmount() });
    }, 300);
    return () => window.clearInterval(id);
  }, [uiState, getBufferedAmount]);

  const stopEverything = useCallback(async () => {
    if (pauseTimerRef.current !== null) {
      window.clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    // Flashlight off as soon as we stop scanning — the camera view is being
    // replaced by results and the drain doesn't need it. The stream is still
    // live here (camera.stop hasn't run), so the constraint applies.
    void camera.setTorch(false);
    setStartBusy(true);
    try {
      await stop();
    } finally {
      if (mountedRef.current) setStartBusy(false);
    }
  }, [stop, camera]);

  useEffect(() => {
    function onVisibilityChange() {
      const current = uiStateRef.current;
      if (current !== 'streaming' && current !== 'paused') return;
      if (document.hidden) {
        setUiState('paused');
        pauseTimerRef.current = window.setTimeout(() => {
          void stopEverything();
          if (mountedRef.current) setUiState('backgrounded_stopped');
        }, BACKGROUNDED_PAUSE_WINDOW_MS);
      } else {
        if (pauseTimerRef.current !== null) {
          window.clearTimeout(pauseTimerRef.current);
          pauseTimerRef.current = null;
        }
        setUiState('streaming');
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [stopEverything]);

  const handleStart = useCallback(() => {
    setCount(0);
    setLastCode(null);
    setServerDrops(0);
    setMetrics(null);
    setProgress(null);
    uploadedRef.current = 0;
    setClientStats({ uploaded: 0, bufferedBytes: 0 });
    setLiveCodes(new Map());
    setUiState('streaming');
    // Flashlight on for the run so coded labels read well in dim aisles;
    // stopEverything turns it back off. Best-effort — no-ops where torch
    // isn't supported (e.g. iOS Safari).
    void camera.setTorch(true);
    start(resolveWsUrl());
  }, [start, camera]);

  const handleStop = useCallback(() => {
    // Stop sending frames immediately and enter the "stopping" state so
    // the results view takes over. The WS itself stays open (server holds
    // it) until the LLM tail drains, at which point the metrics event
    // arrives and the socket closes. Kicking off stopEverything as
    // fire-and-forget — we don't want the button click to hang while the
    // LLM finishes.
    setUiState('stopping');
    void stopEverything();
  }, [stopEverything]);

  const onFrame = useCallback(
    async (frame: SampledFrame) => {
      try {
        // Backpressure: if the socket's outbound buffer is already backed up,
        // drop this frame rather than pile onto a growing backlog. Bailing
        // before the encode also saves the CPU/battery of a frame we'd only
        // queue. The server tolerates dropped frames (it reports them as
        // backpressure); an ever-growing send buffer it can't fix.
        if (getBufferedAmount() > MAX_WS_BUFFER_BYTES) return;
        const blob = await frame.encodeDecodedJpeg();
        sendBinary(blob);
        uploadedRef.current += 1;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[cycle-count] onFrame error', err);
      }
    },
    [getBufferedAmount, sendBinary],
  );

  const cameraReady = camera.status === 'ready';
  const samplerEnabled = uiState === 'streaming' && socketStatus === 'open';

  const samplerArgs = useMemo(
    () => ({
      videoRef: camera.videoRef,
      enabled: samplerEnabled,
      videoReady: cameraReady,
      onFrame,
      frameIntervalMs: 0,
      targetLongEdgePx: 1280,
    }),
    [camera.videoRef, samplerEnabled, cameraReady, onFrame],
  );

  useFrameSampler(samplerArgs);

  const stopDisabled = uiState !== 'streaming' && uiState !== 'paused';
  const showCamera = uiState === 'streaming' || uiState === 'paused';
  const showResults =
    uiState === 'stopping' || uiState === 'closed' || uiState === 'backgrounded_stopped';
  const isProcessing = uiState === 'stopping' && !metrics;

  // Codes list to render — live rollup during streaming/stopping; the
  // final metrics snapshot once it arrives.
  const codesForDisplay: readonly CodeSummary[] = useMemo(() => {
    if (metrics) return metrics.codes;
    return Array.from(liveCodes.values())
      .map(toCodeSummary)
      .sort((a, b) => b.frameCount - a.frameCount || a.code.localeCompare(b.code));
  }, [metrics, liveCodes]);

  return (
    // -mx/-my cancels MainLayout's padding so we can fill the viewport
    // remainder. `dvh` accounts for iOS Safari's dynamic URL bar.
    <div className="-mx-4 -my-8 sm:-mx-6 lg:-mx-8 flex h-[calc(100dvh-8rem)] flex-col overflow-hidden bg-gray-950 text-white">
      {(camera.status === 'error' || camera.status === 'denied') && (
        <div role="alert" className="border-b border-red-800 bg-red-900/50 p-3 text-sm text-red-100">
          <span className="font-medium">Camera permission required.</span>{' '}
          {camera.error && <span>{camera.error}</span>}{' '}
          <button
            type="button"
            onClick={() => void camera.start()}
            className="ml-2 rounded bg-red-700 px-2 py-0.5 text-xs hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      )}

      {uiState === 'backgrounded_stopped' && (
        <div role="alert" className="border-b border-amber-800 bg-amber-900/40 p-3 text-sm text-amber-100">
          Backgrounded — run ended. Tap Start to begin a new run.
        </div>
      )}

      {uiState === 'paused' && (
        <div role="status" aria-live="polite" className="border-b border-blue-800 bg-blue-900/40 p-3 text-sm text-blue-100">
          Paused (tab hidden). Return to this tab to resume.
        </div>
      )}

      {/* Main region: camera OR results OR idle-start */}
      <div className="relative flex-1 overflow-hidden">
        {/* Video element ALWAYS in DOM so useCamera.start() can attach
            srcObject at mount time. Toggle visibility via CSS, not
            conditional rendering, otherwise the stream is acquired
            before the ref exists and the video stays blank. */}
        <video
          ref={camera.videoRef}
          aria-hidden="true"
          playsInline
          muted
          className={`absolute inset-0 h-full w-full object-cover ${showCamera ? '' : 'invisible'}`}
        />

        {/* Idle: giant Start button, no camera */}
        {uiState === 'idle' && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-950">
            <button
              type="button"
              onClick={handleStart}
              disabled={!cameraReady}
              aria-disabled={!cameraReady}
              data-testid="cycle-count-start"
              className="rounded-full bg-emerald-600 px-10 py-6 text-2xl font-semibold text-white shadow-lg hover:bg-emerald-500 disabled:opacity-40 disabled:hover:bg-emerald-600"
            >
              {cameraReady ? 'Start scan' : 'Camera warming up…'}
            </button>
          </div>
        )}

        {/* Streaming overlay: live count top-center, Stop bottom-center */}
        {showCamera && (
          <>
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              aria-label="Cycle Count unique code count"
              data-testid="cycle-count-live-count"
              className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-black/60 px-6 py-3 text-center backdrop-blur"
            >
              <div className="text-5xl font-bold text-emerald-400 tabular-nums">{count}</div>
              {lastCode && (
                <div className="mt-1 max-w-xs truncate text-xs text-gray-300">last: {lastCode}</div>
              )}
              <div className="mt-1 text-xs text-gray-300 tabular-nums">
                sent {clientStats.uploaded} · buffered {Math.round(clientStats.bufferedBytes / 1024)} KB
              </div>
              {progress && (
                <div className="text-xs text-gray-400 tabular-nums">
                  server {progress.framesProcessed}/{progress.framesReceived}
                  {progress.framesReceived - progress.framesProcessed > 0 && (
                    <span className="text-amber-300"> ({progress.framesReceived - progress.framesProcessed} queued)</span>
                  )}
                </div>
              )}
            </div>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
              <button
                type="button"
                onClick={handleStop}
                disabled={stopDisabled}
                aria-disabled={stopDisabled}
                data-testid="cycle-count-stop"
                className="rounded-full bg-red-600 px-10 py-4 text-lg font-semibold text-white shadow-lg hover:bg-red-500 disabled:opacity-40 disabled:hover:bg-red-600"
              >
                Stop
              </button>
            </div>

            {serverDrops > 0 && (
              <div className="absolute bottom-24 left-1/2 -translate-x-1/2 rounded bg-amber-900/70 px-3 py-1 text-xs text-amber-100">
                Backpressure — server dropped {serverDrops}
              </div>
            )}
          </>
        )}

        {/* Results view — replaces camera on stop */}
        {showResults && (
          <div className="flex h-full flex-col overflow-hidden bg-gray-950">
            <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-400">
                  {isProcessing ? 'Processing…' : 'Run complete'}
                </div>
                <div className="text-2xl font-semibold text-emerald-400 tabular-nums">
                  {count} unique codes
                </div>
                <div className="mt-1 text-sm text-gray-300 tabular-nums" data-testid="cycle-count-progress">
                  sent {clientStats.uploaded} · buffered {Math.round(clientStats.bufferedBytes / 1024)} KB
                  {progress && (
                    <span className="text-gray-400">
                      {' · '}server {progress.framesProcessed}/{progress.framesReceived}
                      {progress.framesReceived - progress.framesProcessed > 0 && (
                        <span className="text-amber-300">
                          {' '}({progress.framesReceived - progress.framesProcessed} unprocessed)
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleStart}
                disabled={!cameraReady}
                aria-disabled={!cameraReady}
                data-testid="cycle-count-restart"
                className="rounded bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-40"
              >
                {isProcessing ? 'Scan again' : 'New scan'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <ResultsTable codes={codesForDisplay} />
              {metrics && <MetricsPanel metrics={metrics} />}
              {isProcessing && (
                <p className="text-xs text-gray-500">
                  {progress && progress.framesReceived - progress.framesProcessed > 0
                    ? `Draining ${progress.framesReceived - progress.framesProcessed} unprocessed frame(s), then counting items. Codes update as results arrive.`
                    : 'Waiting for the LLM to finish counting items. Codes will update as results arrive.'}
                </p>
              )}
              {debugLog.length > 0 && (
                <details className="rounded bg-gray-900/60 p-3 text-xs text-gray-400">
                  <summary className="cursor-pointer text-gray-300">
                    Debug ({debugLog.length} events)
                  </summary>
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono">
                    {debugLog.join('\n')}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status bar — thin footer with live status */}
      <div className="border-t border-gray-800 bg-gray-900 px-4 py-1 text-xs text-gray-400">
        <span data-testid="cycle-count-status">
          {uiState} • socket:{socketStatus}
        </span>
        {startBusy && <span className="ml-2 text-gray-500">(closing…)</span>}
      </div>
    </div>
  );
}
